import React, { useEffect, useRef, useLayoutEffect } from "react";
import { NavLink } from "react-router-dom";
import { DiscordIcon, FacebookIcon } from "./ProviderIcons";

// Site footer for the shell pages (lobby/news/leaderboard/settings + legal
// pages): a community icon row over a centered, muted legal line. The Terms /
// Privacy links are internal routes, they also double as the public URLs
// Google's OAuth brand verification requires.

// Skill Check Games community server (shared across our games). This invite
// lands in the Achtung Royale general channel; the server-wide invite linked
// from skillcheckgames.com is discord.gg/ZyXZP9k6f5.
export const DISCORD_INVITE_URL = "https://discord.gg/uqMGcrcJWN";
// The game's Facebook Page (was Achtung Online, facebook.com/achtungonline).
export const FACEBOOK_URL = "https://www.facebook.com/achtungroyale";

const YEAR = new Date().getFullYear();

// `inline`: the copy rendered at the END of a section panel's scroll area on
// phones (see SectionModal), so the footer is something you scroll to rather
// than a strip that is always on screen. It publishes no height: the shell's
// own footer is hidden then, and its 0 is what the panel layer should reserve.
export default function Footer({ inline = false }: { inline?: boolean }) {
  // (Measured before the first paint, a layout effect: pages sized by it, the
  // start page's two columns, would otherwise be drawn once with the guess and
  // then jump to the real height.)
  // Publish the footer's rendered height as --footer-h on the root, so the
  // fixed section-modal layer can reserve exactly that much at the bottom
  // instead of a guess: on phones the legal line wraps and the footer grows,
  // and a fixed reservation let the panel run underneath it.
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (inline || !el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--footer-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--footer-h");
    };
  }, [inline]);
  return (
    <footer className={inline ? "app-footer app-footer--inline" : "app-footer"} ref={ref}>
      <div className="app-footer-inner">
        <div className="app-footer-social">
          <a
            className="app-footer-icon"
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Join our Discord"
            title="Join our Discord"
          >
            <DiscordIcon fill="currentColor" />
          </a>
          <a
            className="app-footer-icon"
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Facebook"
            title="Follow us on Facebook"
          >
            <FacebookIcon fill="currentColor" />
          </a>
        </div>
        <div className="app-footer-legal">
          <NavLink to="/terms">Terms of Service</NavLink>
          <span className="app-footer-dot" aria-hidden="true" />
          <span className="app-footer-copy">© {YEAR} Achtung Royale</span>
          <span className="app-footer-dot" aria-hidden="true" />
          <NavLink to="/privacy">Privacy Policy</NavLink>
        </div>
      </div>
    </footer>
  );
}
